import { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch } from '../../../store'
import { createTeamMembership } from '../../../store/slices/homebotNetwork/clientTeamMemberships'
import { useVerificationFlow } from '../../../Components/shared/VerificationFlow'
import * as Sentry from '@sentry/browser'
import { selectClientId } from '../../../store/selectors/client'

export const useAddPartnerStep = () => {
  const dispatch = useDispatch<AppDispatch>()
  const { state, dispatch: dispatchVerificationFlow } = useVerificationFlow()
  const [isLoading, setIsLoading] = useState(false)
  const clientId = useSelector(selectClientId) as string
  const { currentPartnerType, removedPartnerType } = state

  // Determine which partner type to create based on what was removed
  // If a partner was removed, create the same type, otherwise use currentPartnerType
  // This ensures that when a user removes a RealEstateAgent, they can add a new RealEstateAgent
  // and when they remove a LoanOfficer, they can add a new LoanOffice
  const partnerTypeToCreate = removedPartnerType || currentPartnerType

  const onCancel = () => {
    dispatchVerificationFlow({ type: 'CANCEL' })

    // If they don't add a preferred agent, pull the
    // escape hatch and go to the original
    // schedule tour flow
    if (state.onSkip) {
      state.onSkip()
    }
  }

  const addPartner = async data => {
    setIsLoading(true)

    try {
      const action = await dispatch(
        createTeamMembership({
          clientId,
          preferredAgentFirstName: data.firstName,
          preferredAgentLastName: data.lastName,
          preferredAgentEmail: data.email,
          isVerified: true,
          type: partnerTypeToCreate
        })
      )

      if (action.meta.requestStatus === 'fulfilled') {
        // Include the partner type and membership data in the payload
        dispatchVerificationFlow({
          type: 'PARTNER_ADDED',
          payload: {
            partnerData: { ...data, type: partnerTypeToCreate },
            membership: action.payload,
            partnerType: partnerTypeToCreate
          }
        })
      }
    } catch (error) {
      Sentry.captureException(error)
      throw error
    } finally {
      setIsLoading(false)
    }
  }

  return {
    isLoading,
    onCancel,
    addPartner
  }
}
